import { get, isNil } from 'lodash'
import type { Get } from 'type-fest'

/**
 * Create a Map by associating each object in an array with the value of `key`.
 * If multiple objects have the same key, the last occurrence of the object will map to that key.
 * Null keyed values are not added to the output.
 * @param arr The array of objects to be mapped
 * @param key The key or key-path on each object which will index the Map
 * @returns Map<Value, T>
 *
 * Example:
 * const arr = [{ id: 1, name: 'Alice' }, { id: 2, name: 'Bob' }]
 * const map = associateBy({ arr, key: 'id' })
 * // Map { 1 => { id: 1, name: 'Alice' }, 2 => { id: 2, name: 'Bob' } }
 *
 * Example:
 * const arr = [{ id: 1, details: { name: 'Alice' } }, { id: 2, details: { name: 'Bob' } }]
 * const map = associateBy({ arr, key: 'details.name' })
 * // Map { 'Alice' => { id: 1, details: { name: 'Alice' } }, 'Bob' => { id: 2, details: { name: 'Bob' } } }
 */
export function associateBy<
  T extends object,
  Path extends string | readonly string[],
>({
  arr,
  key,
}: {
  arr: readonly T[]
  key: Path
}): Map<NonNullable<Get<T, Path>>, T> {
  return arr.reduce((agg, item) => {
    const keyedValue = get(item, key)
    if (isNil(keyedValue)) return agg
    agg.set(keyedValue, item)

    return agg
  }, new Map<Get<T, Path>, T>())
}

/**
 * Create a Map by grouping objects in an array by the value of `key`.
 * Null keyed values are not added to the output.
 * @param arr The array of objects to be grouped
 * @param key The key or key-path on each object which will index the Map
 * @returns Map<Value, T[]>
 *
 * Example:
 * const arr = [{ id: 1, name: 'Alice' }, { id: 2, name: 'Bob' }, { id: 3, name: 'Alice' }]
 * const map = groupBy({ arr, key: 'name' })
 * // Map { 'Alice' => [{ id: 1, name: 'Alice' }, { id: 3, name: 'Alice' }], 'Bob' => [{ id: 2, name: 'Bob' }] }
 *
 * Example:
 * const arr = [{ id: 1, details: { name: 'Alice' } }, { id: 2, details: { name: 'Bob' } }, { id: 3, details: { name: 'Alice' } }]
 * const map = groupBy({ arr, key: 'details.name' })
 * // Map { 'Alice' => [{ id: 1, details: { name: 'Alice' } }, { id: 3, details: { name: 'Alice' } }], 'Bob' => [{ id: 2, details: { name: 'Bob' } }] }
 */
export function groupBy<
  T extends object,
  Path extends string | readonly string[],
>({
  arr,
  key,
}: {
  arr: readonly T[]
  key: Path
}): Map<NonNullable<Get<T, Path>>, T[]> {
  return arr.reduce((agg, item) => {
    const keyedValue = get(item, key)
    if (isNil(keyedValue)) return agg

    const currArr = agg.get(keyedValue) || []
    currArr.push(item)
    agg.set(keyedValue, currArr)

    return agg
  }, new Map<Get<T, Path>, T[]>())
}
